import React, { useState } from 'react';
import { usePrefs, DEFAULT_PREFS } from '../hooks/usePrefs';

const PITCH_LANGS = [
  { value: 'fr-qc', label: 'Français québécois' },
  { value: 'fr',    label: 'Français standard'   },
  { value: 'en',    label: 'English'              },
];

const PITCH_TONES = [
  { value: 'professional', label: 'Professionnel' },
  { value: 'casual',       label: 'Décontracté'   },
  { value: 'direct',       label: 'Direct & court' },
];

const SCORE_FILTERS = [
  { value: 100, label: 'Tous les leads' },
  { value: 60,  label: 'Score ≤ 60 (chauds + tièdes)' },
  { value: 30,  label: 'Score ≤ 30 (chauds seulement)' },
];

const RESULT_COUNTS = [10, 15, 20];

export default function PreferencesSection() {
  const { prefs, save, reset } = usePrefs();
  const [draft, setDraft]   = useState(prefs);
  const [saved,  setSaved]  = useState(false);

  function set(key, val) {
    setDraft(prev => ({ ...prev, [key]: val }));
  }

  function handleSave() {
    save(draft);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  }

  function handleReset() {
    if (!window.confirm('Remettre tous les paramètres par défaut ?')) return;
    reset();
    setDraft(DEFAULT_PREFS);
  }

  const inp = {
    background: '#0a0a0d', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 8, padding: '10px 13px', color: '#EAEAEA',
    fontSize: '0.86rem', outline: 'none', width: '100%', boxSizing: 'border-box',
  };

  const label = (txt) => (
    <div style={{ fontSize: '0.63rem', color: '#444', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 }}>{txt}</div>
  );

  const card = (title, icon, children) => (
    <div style={{ background: '#0d0d10', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 14, marginBottom: 20, overflow: 'hidden' }}>
      <div style={{ padding: '14px 22px', borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 18 }}>{icon}</span>
        <span style={{ color: '#EAEAEA', fontWeight: 800, fontSize: '0.92rem' }}>{title}</span>
      </div>
      <div style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {children}
      </div>
    </div>
  );

  const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 };

  const toggle = (key) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ color: '#888', fontSize: '0.83rem' }}>{key === 'autoDetectTech' ? 'Analyser la technologie automatiquement au chargement' : key}</div>
      <button onClick={() => set(key, !draft[key])}
        style={{ width: 42, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer', transition: 'background 0.2s',
          background: draft[key] ? '#C8FF00' : '#1c1c1c', position: 'relative' }}>
        <div style={{ width: 18, height: 18, borderRadius: '50%', background: draft[key] ? '#000' : '#444',
          position: 'absolute', top: 3, left: draft[key] ? 21 : 3, transition: 'left 0.2s' }} />
      </button>
    </div>
  );

  const pills = (key, options) => (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {options.map(o => {
        const active = draft[key] === (o.value ?? o);
        return (
          <button key={o.value ?? o} onClick={() => set(key, o.value ?? o)}
            style={{ background: active ? 'rgba(200,255,0,0.08)' : 'transparent',
              border: `1px solid ${active ? 'rgba(200,255,0,0.35)' : 'rgba(255,255,255,0.07)'}`,
              color: active ? '#C8FF00' : '#444', padding: '6px 14px', borderRadius: 20,
              cursor: 'pointer', fontSize: '0.78rem', fontWeight: active ? 700 : 400, transition: 'all 0.12s' }}>
            {o.label ?? o}
          </button>
        );
      })}
    </div>
  );

  return (
    <div style={{ padding: 32, maxWidth: 820 }}>

      {/* Header */}
      <div style={{ marginBottom: 30 }}>
        <div style={{ fontSize: '0.63rem', color: '#333', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 6 }}>Ucreative</div>
        <h1 style={{ fontSize: '2rem', fontWeight: 900, color: '#EAEAEA', letterSpacing: '-0.04em', margin: '0 0 6px' }}>Préférences</h1>
        <p style={{ color: '#333', fontSize: '0.83rem', margin: 0 }}>
          Ces paramètres sont sauvegardés localement et utilisés dans les pitchs IA, courriels d'audit et l'interface.
        </p>
      </div>

      {/* ── PROFIL AGENCE ── */}
      {card('Profil de l\'agence', '🏢', <>
        <div>
          {label('Nom de l\'agence')}
          <input style={inp} value={draft.agencyName} onChange={e => set('agencyName', e.target.value)} placeholder="Ucreative Inc." />
        </div>
        <div style={grid2}>
          <div>
            {label('Courriel de contact')}
            <input style={inp} type="email" value={draft.agencyEmail} onChange={e => set('agencyEmail', e.target.value)} placeholder="hello@ucreative.ca" />
          </div>
          <div>
            {label('Téléphone')}
            <input style={inp} type="tel" value={draft.agencyPhone} onChange={e => set('agencyPhone', e.target.value)} placeholder="(514) 000-0000" />
          </div>
        </div>
        <div style={grid2}>
          <div>
            {label('Site web')}
            <input style={inp} value={draft.agencyWebsite} onChange={e => set('agencyWebsite', e.target.value)} placeholder="ucreative.ca" />
          </div>
          <div>
            {label('Ville / région')}
            <input style={inp} value={draft.agencyCity} onChange={e => set('agencyCity', e.target.value)} placeholder="Montréal, QC" />
          </div>
        </div>
        <div>
          {label('Slogan / tagline')}
          <input style={inp} value={draft.agencyTagline} onChange={e => set('agencyTagline', e.target.value)} placeholder="Agence web & marketing digital — Montréal" />
        </div>
        <div>
          {label('Couleur accent (courriel & interface)')}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <input type="color" value={draft.accentColor} onChange={e => set('accentColor', e.target.value)}
              style={{ width: 44, height: 36, borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer', background: 'none', padding: 3 }} />
            <input style={{ ...inp, width: 110 }} value={draft.accentColor} onChange={e => set('accentColor', e.target.value)} placeholder="#C8FF00" />
            <span style={{ color: '#333', fontSize: '0.75rem' }}>Utilisé dans le template de courriel d'audit</span>
          </div>
        </div>
      </>)}

      {/* ── LEAD FINDER ── */}
      {card('Lead Finder', '🎯', <>
        <div>
          {label('Ville / région par défaut')}
          <input style={inp} value={draft.defaultLocation} onChange={e => set('defaultLocation', e.target.value)} placeholder="Montréal, QC" />
          <div style={{ color: '#333', fontSize: '0.72rem', marginTop: 5 }}>Pré-remplie à chaque nouvelle recherche</div>
        </div>
        <div>
          {label('Filtre de score par défaut')}
          {pills('defaultMaxScore', SCORE_FILTERS)}
        </div>
        <div>
          {label('Nombre de résultats par recherche')}
          {pills('resultCount', RESULT_COUNTS)}
        </div>
        <div>
          {toggle('autoDetectTech')}
          <div style={{ color: '#2a2a2a', fontSize: '0.7rem', marginTop: 4 }}>Analyse la plateforme et les failles de tous les leads dès que la recherche se termine</div>
        </div>
      </>)}

      {/* ── COURRIEL D'AUDIT ── */}
      {card('Courriel d\'audit de sécurité', '📨', <>
        <div>
          {label('Nom d\'affichage expéditeur')}
          <input style={inp} value={draft.senderName} onChange={e => set('senderName', e.target.value)} placeholder="Steve — Ucreative Inc." />
          <div style={{ color: '#333', fontSize: '0.72rem', marginTop: 5 }}>Apparaît comme "De : [nom]" dans la boîte de réception du client</div>
        </div>
        <div>
          {label('Signature / note de bas de courriel (optionnel)')}
          <textarea value={draft.emailFooter} onChange={e => set('emailFooter', e.target.value)}
            placeholder="Ex: Ce rapport est confidentiel. Pour toute question, communiquez avec nous au (514) 000-0000."
            rows={3}
            style={{ ...inp, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }} />
        </div>
      </>)}

      {/* ── PITCH IA ── */}
      {card('Pitch IA (Claude)', '✨', <>
        <div>
          {label('Langue des pitchs générés')}
          {pills('pitchLang', PITCH_LANGS)}
        </div>
        <div>
          {label('Ton des messages')}
          {pills('pitchTone', PITCH_TONES)}
        </div>
        <div style={{ background: 'rgba(200,255,0,0.04)', border: '1px solid rgba(200,255,0,0.1)', borderRadius: 8, padding: '12px 14px' }}>
          <div style={{ color: '#C8FF00', fontSize: '0.72rem', fontWeight: 700, marginBottom: 4 }}>✅ Téléphone réel dans les pitchs</div>
          <div style={{ color: '#555', fontSize: '0.75rem' }}>
            Le numéro configuré dans <strong style={{ color: '#888' }}>Profil agence → Téléphone</strong> remplacera automatiquement le numéro fictif (514) 000-0000 dans tous les pitchs générés.
          </div>
        </div>
      </>)}

      {/* ── PIPELINE ── */}
      {card('Étapes du pipeline', '📋', <>
        <div style={{ color: '#555', fontSize: '0.78rem', marginBottom: 4 }}>Noms et couleurs des étapes de suivi affichées sur chaque fiche lead.</div>
        {draft.pipelineStages.map((st, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="color" value={st.color}
              onChange={e => {
                const stages = [...draft.pipelineStages];
                stages[i] = { ...stages[i], color: e.target.value };
                set('pipelineStages', stages);
              }}
              style={{ width: 36, height: 34, borderRadius: 6, border: '1px solid rgba(255,255,255,0.08)', cursor: 'pointer', background: 'none', padding: 2, flexShrink: 0 }} />
            <input value={st.label}
              onChange={e => {
                const stages = [...draft.pipelineStages];
                stages[i] = { ...stages[i], label: e.target.value };
                set('pipelineStages', stages);
              }}
              style={{ ...inp, flex: 1 }} />
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: st.color, flexShrink: 0 }} />
          </div>
        ))}
      </>)}

      {/* ── ACTIONS ── */}
      <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
        <button onClick={handleSave}
          style={{ background: saved ? 'rgba(74,222,128,0.1)' : '#C8FF00',
            color: saved ? '#4ade80' : '#000', border: saved ? '1px solid rgba(74,222,128,0.3)' : 'none',
            padding: '13px 32px', borderRadius: 10, fontWeight: 900, fontSize: '0.9rem',
            cursor: 'pointer', transition: 'all 0.2s' }}>
          {saved ? '✓ Préférences sauvegardées' : 'Sauvegarder les préférences'}
        </button>
        <button onClick={handleReset}
          style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.08)',
            color: '#333', padding: '13px 22px', borderRadius: 10,
            fontWeight: 600, fontSize: '0.86rem', cursor: 'pointer' }}>
          Remettre par défaut
        </button>
      </div>

    </div>
  );
}
